import { ElasticSearchQueryWhere } from '../config/asyncGetters/createAsyncGetters';
import { Duration, duration } from './duration';

// Passing a value of a known type to this function produces an error.
// This makes it useful in TS exhaustive checks:
// https://basarat.gitbooks.io/typescript/docs/types/discriminated-unions.html#exhaustive-checks
export function assertNever(value: never, errMsg = 'Invalid value'): never {
  // We throw an error in case the TS type of `value` is different from its run-time type.
  throw new Error(`${errMsg}: '${value}'`);
}

export function clamp(value: number, min: number, max: number): number {
  if (min > max) return clamp(value, max, min);

  return Math.min(Math.max(value, min), max);
}

/**
 * Modulo fixing javascript's negative modulo bug.
 * @param value - The value to perform the modulo operation on.
 * @param mod - The modulo value.
 * @returns - The result of the modulo operation.
 */
export function mod(value: number, mod: number = 1): number {
  return ((value % mod) + mod) % mod;
}

export function safeParseJSON<T>(json: string, defaultValue?: T): T {
  try {
    const value = JSON.parse(json);
    if (value) {
      return value;
    }
  } catch {
    // fall back to defaultValue
  }

  return defaultValue as T;
}

// A query without the option to pass a logical OR
export type SingularElasticSearchQuery = ElasticSearchQueryWhere & {
  length?: never;
};

/**
 * Create a logical OR query from a base query and a list of variants
 * @param where The base query
 * @param variants The variants to be added to the base query
 * @returns Array of the variants to be passed to ES
 */
export function whereVariant(
  where: SingularElasticSearchQuery,
  variants: SingularElasticSearchQuery[],
): ElasticSearchQueryWhere {
  if (variants.length === 0) {
    return where;
  }

  return variants.map((variant) => ({
    ...where,
    ...variant,
  })) as ElasticSearchQueryWhere;
}

export function roundTimestampUpTo(
  timestamp: number,
  roundBy: Duration,
): number {
  const durationMs = duration(roundBy);
  return Math.ceil(timestamp / durationMs) * durationMs;
}
